<template>
    <ul>
        <template
            v-for="(item, i) in model"
            :key="item"
        >
            <app-menu-item
                :item="item"
                root
                :index="i"
            />

            <li class="menu-separator"></li>
        </template>
    </ul>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouteLink } from '~/composables/routing'
import { useTicketsEnabled } from '~/composables/use-ticket'
import { useRpQuery } from '~/composables/graphql'
import { useQuery } from '@tanstack/vue-query'
import { graphql } from '~/resources/graphql'
import AppMenuItem from '~/components/Layouts/ProfileLayout/AppMenuItem.vue'
import { useRouter } from 'vue-router'
import {
    useSelectedCustomer,
    useSetSelectedCustomer
} from '~/components/Layouts/ProfileLayout/composables/use-selected-customer'
import { useSignOut } from '~/composables/use-login'
import { useCheckInHistoryVisible } from '~/composables/use-org'
import { usePostSignOut } from '~/composables/mobile/use-post-message'
import { useLayoutMode } from '~/composables/use-layout-mode'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const setSelectedCustomer = useSetSelectedCustomer()
const selectedCustomer = useSelectedCustomer()
const isTicketsEnabled = useTicketsEnabled()
const isCheckInHistoryVisible = useCheckInHistoryVisible()
const { signOut } = useSignOut()
const mobileAppSignOut = usePostSignOut()
const { isMobileApp } = useLayoutMode()
const { t } = useI18n({ useScope: 'global' })
const {
    profileBookingsLink,
    profileFriendsAndFamilyLink,
    profileCustomerInfoLink,
    profileCheckInHistoryLink,
    profileBenefitsAndDiscountsLink,
    profileInvoicesLink,
    profileLink,
    profileMembershipsLink,
    profilePassesLink,
    profileContactUsLink,
    profilePaymentMethodsLink,
    profileDashboardLink,
    profileWaitlistsLink
} = useRouteLink()
const model = computed(() => {
    const accountItems: {
        label: string
        icon: string
        to?: string
        command?: () => void
    }[] = [
        {
            label: t('checkoutSuccess.summary'),
            icon: 'pi pi-fw pi-home',
            to: profileLink()
        },
        {
            label: t('layout.friendsAndFamily'),
            icon: 'pi pi-fw pi-star',
            to: profileFriendsAndFamilyLink()
        },
        {
            label: t('layout.orderHistory'),
            icon: 'pi pi-fw pi-shopping-bag',
            to: profileInvoicesLink()
        },
        {
            label: t('membership', 2),
            icon: 'pi pi-fw pi-tag',
            to: profileMembershipsLink()
        },
        {
            label: t('layout.bookings'),
            icon: 'pi pi-fw pi-calendar',
            to: profileBookingsLink()
        },
        {
            label: t('layout.passes'),
            icon: 'pi pi-fw pi-ticket',
            to: profilePassesLink()
        },
        {
            label: t('layout.paymentMethods'),
            icon: 'pi pi-fw pi-credit-card',
            to: profilePaymentMethodsLink()
        },
        {
            label: t('layout.waitlistStatus'),
            icon: 'pi pi-fw pi-clock',
            to: profileWaitlistsLink()
        }
    ]

    if (isCheckInHistoryVisible.value) {
        accountItems.push({
            label: t('layout.checkIns'),
            icon: 'pi pi-fw pi-map-marker',
            to: profileCheckInHistoryLink()
        })
    }

    accountItems.push(
        {
            label: t('layout.benefitsAndDiscounts'),
            icon: 'pi pi-fw pi-list',
            to: profileBenefitsAndDiscountsLink()
        },
        // {
        //     label: 'Agreements',
        //     icon: 'pi pi-fw pi-file',
        //     to: profileAgreementsLink()
        // },
        {
            label: t('layout.customerInfo'),
            icon: 'pi pi-fw pi-info-circle',
            to: profileCustomerInfoLink()
        }
    )

    const organization = viewerData.value?.organization
    if (
        organization !== undefined &&
        organization !== null &&
        organization.contactUsLinkText !== null
    ) {
        accountItems.push({
            label: t('contactUs'),
            icon: 'pi pi-fw pi-envelope',
            to: profileContactUsLink()
        })
    }

    const viewer = viewerData.value?.viewer
    if (viewer !== undefined && viewer !== null && viewer.dashboardAccess) {
        accountItems.push({
            label: t('layout.dashboard'),
            icon: 'pi pi-fw pi-chart-line',
            to: profileDashboardLink()
        })
    }

    accountItems.push({
        label: t('layout.signOut'),
        icon: 'pi pi-fw pi-sign-out',
        command: () => {
            if (isMobileApp.value) {
                mobileAppSignOut()
            } else {
                signOut()
            }
        }
    })

    const items = [
        {
            label: selectedCustomer.value.commaName,
            items: accountItems
        }
    ]

    if (
        viewer !== undefined &&
        viewer !== null &&
        viewer.customers.edges.length > 0
    ) {
        items.push({
            label: t('layout.myAccount'),
            items: [
                {
                    label: t('layout.me'),
                    icon: 'pi pi-fw pi-user',
                    command: () => {
                        setSelectedCustomer({
                            id: viewer.primaryCustomer.id,
                            commaName: viewer.primaryCustomer.commaName,
                            firstName: viewer.primaryCustomer.firstName
                        })
                        router.push(profileLink())
                    }
                },
                ...viewer.customers.edges.map(edge => ({
                    label: edge.node.commaName,
                    icon: 'pi pi-fw pi-users',
                    command: () => {
                        setSelectedCustomer({
                            id: edge.node.id,
                            commaName: edge.node.commaName,
                            firstName: edge.node.firstName
                        })
                        router.push(profileLink())
                    }
                }))
            ]
        })
    }

    return items
})

// TODO: Paging?
const query = useRpQuery()
const { data: viewerData } = useQuery({
    queryKey: ['ViewerCustomersQuery'],
    placeholderData: previousData => previousData,
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query ViewerCustomersQuery {
                    viewer {
                        dashboardAccess
                        primaryCustomer {
                            id
                            commaName
                            firstName
                        }
                        customers(first: 30) {
                            edges {
                                node {
                                    id
                                    commaName
                                    firstName
                                }
                            }
                        }
                    }
                    organization {
                        contactUsLinkText
                    }
                }
            `)
        )
})
</script>
